import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  AcademicCapIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  XCircleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  ArrowPathIcon,
  LockClosedIcon,
  BookOpenIcon,
  UsersIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { formatPhoneNumber } from '@/utils/phoneUtils'
import { 
  getUsers, 
  createUser, 
  deleteUser, 
  getUserStats, 
  type User as ApiUser,
} from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import {
  uploadTeachers,
  downloadFailedRecordsCSV,
  downloadTeacherTemplate,
  type UploadTeachersResponse,
} from '@/api/teachersApi'
import {
  getGrades,
  getStreams,
  getLessons,
  updateLesson,
  updateStream,
  type Grade,
  type Stream,
  type Lesson
} from '@/api/academicApi'
import toast from 'react-hot-toast'

// Form schema for adding new teacher
const addTeacherSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().min(1, 'Phone number is required'),
  countryCode: z.string().min(1, 'Country code is required'),
  classTeacherGradeId: z.string().optional(),
  classTeacherStreamId: z.string().optional(),
})

type AddTeacherData = z.infer<typeof addTeacherSchema>

const TeachersPage: React.FC = () => {
  const [teachers, setTeachers] = useState<ApiUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedTeacher, setSelectedTeacher] = useState<ApiUser | null>(null)
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [gradeFilter, setGradeFilter] = useState<string>('all')
  const [streamFilter, setStreamFilter] = useState<string>('all')
  const [assignmentFilter, setAssignmentFilter] = useState<string>('all')

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<UploadTeachersResponse | null>(null)
  const [uploadProgress, setUploadProgress] = useState<{
    stage: 'uploading' | 'processing' | 'complete'
    percent: number
    message: string
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalTeachers, setTotalTeachers] = useState(0)
  const [pageSize] = useState(10)
  
  // Sorting state
  const [sortBy, setSortBy] = useState<'createdAt' | 'firstName' | 'lastName' | 'email' | 'lastLogin'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Available teacher roles from API
  const [teacherRoles, setTeacherRoles] = useState<{ id: string, name: string }[]>([])
  
  // Stats state
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })

  // Add Teacher Modal - Multi-step state
  const [addModalStep, setAddModalStep] = useState<1 | 2>(1)
  const [createdTeacherId, setCreatedTeacherId] = useState<string | null>(null)

  // Lesson Assignment state
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [lessons, setLessons] = useState<Lesson[]>([])
  const [selectedGradeId, setSelectedGradeId] = useState<string>('')
  const [selectedStreamId, setSelectedStreamId] = useState<string>('')
  const [selectedLessonIds, setSelectedLessonIds] = useState<string[]>([])
  const [assigningLessons, setAssigningLessons] = useState(false)

  // Class Teacher Assignment state (for Step 1)
  const [classTeacherStreams, setClassTeacherStreams] = useState<Stream[]>([])
  const [selectedClassTeacherGradeId, setSelectedClassTeacherGradeId] = useState<string>('')

  const addTeacherForm = useForm<AddTeacherData>({
    resolver: zodResolver(addTeacherSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
      classTeacherGradeId: '',
      classTeacherStreamId: '',
    }
  })

  const fetchTeachers = async () => {
    setLoading(true)
    try {
      // Get all available roles first to identify teacher role IDs
      const rolesResponse = await getRoles()
      const teacherRoleIds = rolesResponse.data
        .filter(role => 
          role.name.toLowerCase().includes('teacher') || 
          role.name.toLowerCase().includes('educator') ||
          role.name.toLowerCase().includes('instructor')
        )
        .map(role => role.id)

      if (teacherRoleIds.length === 0) {
        console.warn('No teacher roles found')
        setTeachers([])
        setLoading(false)
        return
      }

      // For now, use the first teacher role ID found (most common case)
      const primaryTeacherRoleId = teacherRoleIds[0]

      // Fetch teachers with proper pagination (respecting backend limit of 100)
      const response = await getUsers({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        roleId: primaryTeacherRoleId,
        status: statusFilter !== 'all' ? statusFilter as 'active' | 'inactive' : undefined,
        sortBy,
        sortOrder,
      })

      setTeachers(response.users)
      
      // Update pagination info from API response
      if (response.pagination) {
        setCurrentPage(response.pagination.currentPage)
        setTotalPages(response.pagination.totalPages)
        setTotalTeachers(response.pagination.totalUsers)
      }
      
    } catch (error) {
      console.error('Failed to fetch teachers:', error)
      toast.error('Failed to load teachers')
    } finally {
      setLoading(false)
    }
  }

  const fetchTeacherRoles = async () => {
    try {
      const response = await getRoles()
      // Filter only teacher-related roles
      const teacherRolesList = response.data.filter(role => 
        role.name.toLowerCase() === "teacher"
      )
      setTeacherRoles(teacherRolesList.map(role => ({ id: role.id, name: role.name })))
    } catch (error) {
      console.error('Failed to fetch teacher roles:', error)
    }
  }

  const fetchTeacherStats = async () => {
    try {
      // Get stats for the current institution (backend handles filtering)
      const response = await getUserStats()
      
      // Filter stats to only include teacher-related counts
      const teacherStats = Object.entries(response).reduce((acc, [key, value]) => {
        if (key.toLowerCase() === "teacher") {
          acc.byRole[key] = value as number
        }
        return acc
      }, { total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })
      
      setStats(teacherStats)
    } catch (error) {
      console.error('Failed to fetch teacher stats:', error)
    }
  }

  useEffect(() => {
    fetchTeachers()
    fetchTeacherRoles()
  }, [currentPage, searchQuery, statusFilter, sortBy, sortOrder])

  useEffect(() => {
    if (teachers.length > 0) {
      fetchTeacherStats()
    }
  }, [teachers])

  // Debounced search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1) // Reset to first page when filters change
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, statusFilter])

  // Fetch grades when modal opens (needed for both step 1 class teacher and step 2 lesson assignment)
  useEffect(() => {
    if (showAddModal) {
      fetchGrades()
    }
  }, [showAddModal])

  // Fetch streams and lessons when grade is selected (for lesson assignment)
  useEffect(() => {
    if (selectedGradeId) {
      fetchStreams(selectedGradeId)
      fetchLessons(selectedGradeId)
      setSelectedStreamId('')
      setSelectedLessonIds([])
    }
  }, [selectedGradeId])

  // Fetch streams when class teacher grade is selected
  useEffect(() => {
    const fetchClassTeacherStreams = async () => {
      if (selectedClassTeacherGradeId) {
        try {
          const response = await getStreams({ gradeId: selectedClassTeacherGradeId })
          setClassTeacherStreams(response)
          addTeacherForm.setValue('classTeacherStreamId', '')
        } catch (error) {
          console.error('Failed to fetch streams for class teacher:', error)
        }
      } else {
        setClassTeacherStreams([])
      }
    }
    fetchClassTeacherStreams()
  }, [selectedClassTeacherGradeId])

  const fetchGrades = async () => {
    try {
      const response = await getGrades({ status: 'active', limit: 100 })
      setGrades(response.grades)
    } catch (error) {
      console.error('Failed to fetch grades:', error)
      toast.error('Failed to load grades')
    }
  }

  const fetchStreams = async (gradeId: string) => {
    try {
      const response = await getStreams({ gradeId })
      setStreams(response)
    } catch (error) {
      console.error('Failed to fetch streams:', error)
      toast.error('Failed to load streams')
    }
  }

  const fetchLessons = async (gradeId: string) => {
    try {
      const response = await getLessons({ gradeId })
      setLessons(response)
    } catch (error) {
      console.error('Failed to fetch lessons:', error)
      toast.error('Failed to load lessons')
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleDeleteTeacher = async (teacher: ApiUser) => {
    if (confirm(`Are you sure you want to remove ${teacher.firstName} ${teacher.lastName} from teachers?`)) {
      try {
        await deleteUser(teacher.id)
        toast.success(`${teacher.firstName} ${teacher.lastName} removed successfully`)
        fetchTeachers()
      } catch (error) {
        toast.error('Failed to remove teacher')
      }
    }
  }


  const handleAddTeacher = async (teacherData: AddTeacherData) => {
    try {
      // Auto-select the first teacher role
      if (teacherRoles.length === 0) {
        toast.error('No teacher roles found. Please contact administrator.')
        return
      }
      const teacherRoleId = teacherRoles[0].id

      const response = await createUser({
        firstName: teacherData.firstName,
        lastName: teacherData.lastName,
        email: teacherData.email,
        phone: teacherData.phone,
        roleId: teacherRoleId,
        countryCode: teacherData.countryCode
      })

      // Use the correct response structure based on the API return format
      const createdTeacher = response.data?.user
      const teacherId = createdTeacher?._id || createdTeacher?.id
      const teacherName = createdTeacher
        ? `${createdTeacher.firstName} ${createdTeacher.lastName}`
        : `${teacherData.firstName} ${teacherData.lastName}`

      // If a stream was selected for class teacher, update the stream
      if (teacherData.classTeacherStreamId && teacherId) {
        try {
          await updateStream(teacherData.classTeacherStreamId, {
            classTeacher: teacherId
          })
          toast.success(`Teacher ${teacherName} assigned as class teacher!`)
        } catch (error) {
          console.error('Failed to assign class teacher:', error)
          toast.error('Teacher created but failed to assign as class teacher')
        }
      } else {
        toast.success(`Teacher ${teacherName} created successfully!`)
      }

      // Store teacher ID and move to step 2 for lesson assignment
      if (teacherId) {
        setCreatedTeacherId(teacherId)
        setAddModalStep(2)
      } else {
        // If no ID returned, close modal
        closeAddModal()
      }

      fetchTeachers()
    } catch (error: any) {
      console.error('Failed to create teacher:', error)
      toast.error(error?.response?.data?.message || 'Failed to create teacher')
    }
  }

  // Handle lesson assignment to teacher
  const handleAssignLessons = async () => {
    if (!createdTeacherId || selectedLessonIds.length === 0) {
      toast.error('Please select at least one lesson to assign')
      return
    }

    if (!selectedStreamId) {
      toast.error('Please select a stream for the teacher')
      return
    }

    setAssigningLessons(true)
    try {
      // Update each selected lesson to add this teacher
      const updatePromises = selectedLessonIds.map(async (lessonId) => {
        const lesson = lessons.find(l => l.id === lessonId)
        const existingTeacherIds = lesson?.teacherIds || []

        // Only add if not already assigned
        if (!existingTeacherIds.includes(createdTeacherId)) {
          return updateLesson(lessonId, {
            teacherIds: [...existingTeacherIds, createdTeacherId]
          })
        }
        return Promise.resolve()
      })

      await Promise.all(updatePromises)
      toast.success(`${selectedLessonIds.length} lesson(s) assigned successfully!`)
      closeAddModal()
    } catch (error: any) {
      console.error('Failed to assign lessons:', error)
      toast.error(error?.response?.data?.message || 'Failed to assign lessons')
    } finally {
      setAssigningLessons(false)
    }
  }

  // Close modal and reset state
  const closeAddModal = () => {
    setShowAddModal(false)
    setAddModalStep(1)
    setCreatedTeacherId(null)
    setSelectedGradeId('')
    setSelectedStreamId('')
    setSelectedLessonIds([])
    setGrades([])
    setStreams([])
    setLessons([])
    // Reset class teacher selection
    setSelectedClassTeacherGradeId('')
    setClassTeacherStreams([])
    addTeacherForm.reset()
    addTeacherForm.setValue('countryCode', 'KE')
  }

  // Skip lesson assignment
  const handleSkipLessonAssignment = () => {
    toast.success('Teacher created. You can assign lessons later.')
    closeAddModal()
  }

  // Toggle lesson selection
  const toggleLessonSelection = (lessonId: string) => {
    setSelectedLessonIds(prev =>
      prev.includes(lessonId)
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    )
  }

  // Handle file selection for upload
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        toast.error('Please select a CSV file')
        return
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB')
        return
      }
      setUploadFile(file)
      setUploadResult(null)
    }
  }

  // Handle upload submission
  const handleUploadTeachers = async () => {
    if (!uploadFile) {
      toast.error('Please select a CSV file')
      return
    }

    setUploading(true)
    setUploadProgress({ stage: 'uploading', percent: 0, message: 'Starting upload...' })

    try {
      const response = await uploadTeachers(
        uploadFile,
        undefined,
        (progress) => {
          setUploadProgress(progress)
        }
      )
      setUploadResult(response)
      setUploadProgress(null)

      const { successfulCount, failedCount } = response.data.summary

      if (failedCount === 0) {
        toast.success(`Successfully uploaded ${successfulCount} teachers!`)
      } else if (successfulCount === 0) {
        toast.error(`All ${failedCount} teachers failed to upload`)
      } else {
        toast.success(`Uploaded ${successfulCount} teachers, ${failedCount} failed`)
      }

      // Refresh teachers list if any were successful
      if (successfulCount > 0) {
        fetchTeachers()
      }
    } catch (error: any) {
      console.error('Upload failed:', error)
      setUploadProgress(null)
      toast.error(error?.response?.data?.message || 'Failed to upload teachers')
    } finally {
      setUploading(false)
    }
  }

  // Handle download of failed records
  const handleDownloadFailedRecords = () => {
    if (uploadResult?.data?.failedRecordsCSV) {
      try {
        downloadFailedRecordsCSV(
          uploadResult.data.failedRecordsCSV,
          `failed_teachers_${new Date().toISOString().split('T')[0]}.csv`
        )
        toast.success('Failed records downloaded')
      } catch (error) {
        toast.error('Failed to download records')
      }
    }
  }

  // Reset upload modal state
  const resetUploadModal = () => {
    setShowUploadModal(false)
    setUploadFile(null)
    setUploadResult(null)
    setUploadProgress(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <AcademicCapIcon className="h-8 w-8 text-indigo-600 mr-3" />
            Teachers Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage teaching staff and their roles
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="secondary"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
            Upload Teachers
          </Button>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Teacher
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Teachers</p>
              <p className="text-2xl font-bold text-gray-900">{totalTeachers}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Teachers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">With Classes</p>
              <p className="text-2xl font-bold text-gray-900">
                {teachers.filter(t => t.firstName === 'mwangi').length}
              </p>
            </div>
            <BookOpenIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unassigned</p>
              <p className="text-2xl font-bold text-gray-900">
                {teachers.filter(t => t.firstName !== 'mwangi').length}
              </p>
            </div>
            <XCircleIcon className="h-8 w-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="dashboard-card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search teachers by name or email..."
                className="form-input pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={assignmentFilter}
              onChange={(e) => setAssignmentFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Teachers</option>
              <option value="assigned">With Class Assignment</option>
              <option value="unassigned">No Class Assignment</option>
            </select>

            <button
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                showAdvancedSearch 
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Advanced Search
            </button>

            <Button
              onClick={() => {
                setSearchQuery('')
                setStatusFilter('all')
                setCurrentPage(1)
              }}
              variant="secondary"
              className="whitespace-nowrap"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Advanced Search Panel */}
      {showAdvancedSearch && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-indigo-50 border border-indigo-200 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-indigo-900 mb-4">Advanced Teacher Search</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teaching Grade</label>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Grades</option>
                <option value="Grade 1">Grade 1</option>
                <option value="Grade 2">Grade 2</option>
                <option value="Grade 3">Grade 3</option>
                <option value="Grade 4">Grade 4</option>
                <option value="Grade 5">Grade 5</option>
                <option value="Grade 6">Grade 6</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teaching Stream</label>
              <select
                value={streamFilter}
                onChange={(e) => setStreamFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Streams</option>
                <option value="Stream A">Stream A</option>
                <option value="Stream B">Stream B</option>
                <option value="Stream C">Stream C</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Status</label>
              <select
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Teachers</option>
                <option value="assigned">With Class Assignment</option>
                <option value="unassigned">No Class Assignment</option>
                <option value="multiple">Multiple Classes</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-indigo-200">
            <div className="text-sm text-indigo-700">
              💡 Search teachers by their class assignments, teaching grades, and responsibilities
            </div>
            <button
              onClick={() => {
                setGradeFilter('all')
                setStreamFilter('all')
                setAssignmentFilter('all')
                setSearchQuery('')
                setStatusFilter('all')
              }}
              className="px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800"
            >
              Clear All Filters
            </button>
          </div>
        </motion.div>
      )}

      {/* Teachers Table */}
      <div className="dashboard-card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Teachers List ({teachers.length} of {totalTeachers})
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : teachers.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No teachers found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery ? 'Try adjusting your search criteria.' : 'Get started by adding your first teacher.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teacher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teaching Assignments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teachers.map((teacher) => (
                  <tr key={teacher.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                          <UserIcon className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {teacher.firstName} {teacher.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{teacher.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {teacher.isClassTeacher && teacher.classTeacherAssignments?.length > 0 ? (
                          <>
                            <div className="flex items-center space-x-2">
                              <BookOpenIcon className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium text-gray-900">
                                {teacher.classTeacherAssignments.length === 1
                                  ? `${teacher.classTeacherAssignments[0].gradeName} - ${teacher.classTeacherAssignments[0].streamName}`
                                  : `${teacher.classTeacherAssignments[0].gradeName} - Multiple Streams`}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-xs text-gray-600">
                                  {teacher.classTeacherAssignments.map((a: any) => a.streamName).join(', ')}
                                </span>
                              </div>
                              <div className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Class Teacher
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center space-x-2">
                              <BookOpenIcon className="h-4 w-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-500">
                                Class Assignment Pending
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 text-orange-600">
                              <XCircleIcon className="h-4 w-4" />
                              <span className="text-xs">Not Assigned to Class</span>
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{teacher.email}</span>
                        </div>
                        {teacher.phone && (
                          <div className="flex items-center space-x-2">
                            <PhoneIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{formatPhoneNumber(teacher.phone, teacher.countryCode)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        teacher.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {teacher.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {teacher.lastLogin ? new Date(teacher.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTeacher(teacher)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalTeachers)} of {totalTeachers} teachers
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = i + 1
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`btn btn-sm ${pageNum === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      {pageNum}
                    </button>
                  )
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary btn-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Teacher Modal - Multi-step */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeAddModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <AcademicCapIcon className="h-6 w-6 text-indigo-600 mr-2" />
                      {addModalStep === 1 ? 'Add New Teacher' : 'Assign Lessons'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {addModalStep === 1 ? 'Step 1: Create teacher account' : 'Step 2: Assign lessons to teacher (optional)'}
                    </p>
                  </div>
                  <button
                    onClick={closeAddModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Step Indicator */}
                <div className="mt-4 flex items-center space-x-4">
                  <div className={`flex items-center ${addModalStep >= 1 ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      addModalStep >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {addModalStep > 1 ? <CheckCircleIcon className="h-5 w-5" /> : '1'}
                    </div>
                    <span className="ml-2 text-sm font-medium">Teacher Info</span>
                  </div>
                  <div className="flex-1 h-0.5 bg-gray-200">
                    <div className={`h-full transition-all duration-300 ${addModalStep >= 2 ? 'bg-indigo-600 w-full' : 'w-0'}`} />
                  </div>
                  <div className={`flex items-center ${addModalStep >= 2 ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      addModalStep >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-medium">Assign Lessons</span>
                  </div>
                </div>
              </div>

              {/* Step 1: Teacher Information Form */}
              {addModalStep === 1 && (
                <form onSubmit={addTeacherForm.handleSubmit(handleAddTeacher)} className="p-6 space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                      <UserIcon className="h-5 w-5 text-indigo-600 mr-2" />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        {...addTeacherForm.register('firstName')}
                        label="First Name *"
                        placeholder="John"
                        error={addTeacherForm.formState.errors.firstName?.message}
                      />

                      <Input
                        {...addTeacherForm.register('lastName')}
                        label="Last Name *"
                        placeholder="Smith"
                        error={addTeacherForm.formState.errors.lastName?.message}
                      />

                      <Input
                        {...addTeacherForm.register('email')}
                        type="email"
                        label="Email Address *"
                        placeholder="john.smith@school.com"
                        error={addTeacherForm.formState.errors.email?.message}
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <PhoneNumberInput
                          value={addTeacherForm.watch('phone')}
                          countryCode={addTeacherForm.watch('countryCode')}
                          onChange={(phone: string, countryCode: string) => {
                            addTeacherForm.setValue('phone', phone)
                            addTeacherForm.setValue('countryCode', countryCode)
                            addTeacherForm.trigger(['phone', 'countryCode'])
                          }}
                          placeholder="Enter phone number"
                          error={addTeacherForm.formState.errors.phone?.message || addTeacherForm.formState.errors.countryCode?.message}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Class Teacher Assignment (Optional) */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                      <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-2" />
                      Class Teacher Assignment (Optional)
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Assign this teacher as a class teacher for a specific stream
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Grade
                        </label>
                        <select
                          value={selectedClassTeacherGradeId}
                          onChange={(e) => setSelectedClassTeacherGradeId(e.target.value)}
                          className="form-select w-full"
                        >
                          <option value="">Select grade...</option>
                          {grades.map(grade => (
                            <option key={grade.id || grade._id} value={grade.id || grade._id}>{grade.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Stream
                        </label>
                        <select
                          {...addTeacherForm.register('classTeacherStreamId')}
                          className="form-select w-full"
                          disabled={!selectedClassTeacherGradeId}
                        >
                          <option value="">Select stream...</option>
                          {classTeacherStreams.map(stream => (
                            <option key={stream.id || stream._id} value={stream.id || stream._id}>
                              {stream.name}
                              {stream.classTeacher && ` (Current: ${stream.classTeacher})`}
                            </option>
                          ))}
                        </select>
                        {!selectedClassTeacherGradeId && (
                          <p className="mt-1 text-xs text-gray-500">Select a grade first</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Password Info */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <LockClosedIcon className="h-5 w-5 text-indigo-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-indigo-800">Secure Password</p>
                        <p className="text-xs text-indigo-600">Auto-generated and sent via email</p>
                      </div>
                    </div>
                  </div>

                  {/* Teacher Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-medium text-blue-900">Teacher Account Creation</h5>
                        <div className="text-sm text-blue-700 mt-1 space-y-1">
                          <p>• Teacher will receive login credentials via email</p>
                          <p>• After creation, you can assign lessons to this teacher</p>
                          <p>• Account will be created with Teacher role permissions</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button
                      type="button"
                      onClick={closeAddModal}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={addTeacherForm.formState.isSubmitting}
                    >
                      <AcademicCapIcon className="h-4 w-4 mr-2" />
                      {addTeacherForm.formState.isSubmitting ? 'Creating...' : 'Create & Continue'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 2: Lesson Assignment */}
              {addModalStep === 2 && (
                <div className="p-6 space-y-6">
                  {/* Success message */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      <p className="text-sm font-medium text-green-800">
                        Teacher created successfully! Now assign lessons.
                      </p>
                    </div>
                  </div>

                  {/* Grade & Stream Selection */}
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                      <BookOpenIcon className="h-5 w-5 text-indigo-600 mr-2" />
                      Select Grade & Stream
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Grade *
                        </label>
                        <select
                          value={selectedGradeId}
                          onChange={(e) => setSelectedGradeId(e.target.value)}
                          className="form-select w-full"
                        >
                          <option value="">Select a grade...</option>
                          {grades.map(grade => (
                            <option key={grade._id} value={grade._id}>{grade.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Stream *
                        </label>
                        <select
                          value={selectedStreamId}
                          onChange={(e) => setSelectedStreamId(e.target.value)}
                          className="form-select w-full"
                          disabled={!selectedGradeId}
                        >
                          <option value="">Select a stream...</option>
                          {streams.map(stream => (
                            <option key={stream._id} value={stream._id}>{stream.name}</option>
                          ))}
                        </select>
                        {!selectedGradeId && (
                          <p className="mt-1 text-xs text-gray-500">Select a grade first</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Lessons Selection */}
                  {selectedGradeId && selectedStreamId && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                        <BookOpenIcon className="h-5 w-5 text-purple-600 mr-2" />
                        Select Lessons
                        {selectedLessonIds.length > 0 && (
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({selectedLessonIds.length} selected)
                          </span>
                        )}
                      </h4>

                      {lessons.length === 0 ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center text-gray-500">
                          <BookOpenIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                          <p>No lessons found for this grade.</p>
                          <p className="text-sm">Create lessons first to assign them to teachers.</p>
                        </div>
                      ) : (
                        <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                          {lessons.map(lesson => (
                            <label
                              key={lesson.id}
                              className={`flex items-center p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                                selectedLessonIds.includes(lesson.id) ? 'bg-indigo-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedLessonIds.includes(lesson.id)}
                                onChange={() => toggleLessonSelection(lesson.id)}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <div className="ml-3 flex-1">
                                <p className="text-sm font-medium text-gray-900">{lesson.title}</p>
                                <p className="text-xs text-gray-500">
                                  {lesson.subject} • {lesson.duration} mins • {lesson.difficulty}
                                </p>
                              </div>
                              {lesson.teachers && lesson.teachers.length > 0 && (
                                <span className="text-xs text-gray-400">
                                  {lesson.teachers.length} teacher(s) assigned
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info message */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <BookOpenIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-medium text-blue-900">Lesson Assignment</h5>
                        <div className="text-sm text-blue-700 mt-1 space-y-1">
                          <p>• Select a grade and stream to view available lessons</p>
                          <p>• The teacher will be assigned to teach in the selected stream</p>
                          <p>• You can assign multiple lessons to this teacher</p>
                          <p>• Skip this step if you want to assign lessons later</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button
                      type="button"
                      onClick={handleSkipLessonAssignment}
                      variant="secondary"
                    >
                      Skip for Now
                    </Button>
                    <Button
                      type="button"
                      onClick={handleAssignLessons}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      disabled={assigningLessons || selectedLessonIds.length === 0 || !selectedStreamId}
                    >
                      <BookOpenIcon className="h-4 w-4 mr-2" />
                      {assigningLessons ? 'Assigning...' : `Assign ${selectedLessonIds.length} Lesson(s)`}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Teacher Details Modal */}
      <AnimatePresence>
        {selectedTeacher && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedTeacher(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                      <AcademicCapIcon className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedTeacher.firstName} {selectedTeacher.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">Teaching Staff Member</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedTeacher(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Email</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedTeacher.email}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Phone</span>
                        <span className="text-sm text-gray-900 font-medium">{formatPhoneNumber(selectedTeacher.phone, selectedTeacher.countryCode)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          selectedTeacher.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedTeacher.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Academic Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Academic Details</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Role</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedTeacher.role}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Institution</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedTeacher.institution}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Last Login</span>
                        <span className="text-sm text-gray-900 font-medium">
                          {selectedTeacher.lastLogin 
                            ? new Date(selectedTeacher.lastLogin).toLocaleDateString() 
                            : 'Never'
                          }
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Created</span>
                        <span className="text-sm text-gray-900 font-medium">
                          {new Date(selectedTeacher.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Summary */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-4">Teaching Activity</h4>
                  <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-indigo-600">0</div>
                      <div className="text-sm text-gray-600">Active Classes</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">0</div>
                      <div className="text-sm text-gray-600">Lessons Taught</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">0</div>
                      <div className="text-sm text-gray-600">Students</div>
                    </div>
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Account Information</h4>
                  <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Created By: </span>
                      <span className="text-gray-900 font-medium">{selectedTeacher.createdBy || 'System'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Account Type: </span>
                      <span className="text-gray-900 font-medium">Teaching Staff</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => toast.success('Edit teacher details coming soon!')}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Teacher
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Teachers Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={resetUploadModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <ArrowUpTrayIcon className="h-6 w-6 text-indigo-600 mr-2" />
                      Upload Teachers from CSV
                    </h3>
                    <p className="text-sm text-gray-500">Bulk import teachers with lesson assignments</p>
                  </div>
                  <button
                    onClick={resetUploadModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* CSV Format Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-blue-900">CSV Format Requirements</h5>
                      <div className="text-sm text-blue-700 mt-2 space-y-1">
                        <p><strong>Headers:</strong> firstName, lastName, email, phone, countryCode, lessons</p>
                        <p><strong>Lessons format:</strong> Subject:Grade:Stream | Subject:Grade:Stream | ...</p>
                        <p className="text-xs mt-2">Example: <code className="bg-blue-100 px-1 rounded">Math:Grade 1:Stream A | English:Grade 2:Stream B</code></p>
                      </div>
                      <button
                        onClick={downloadTeacherTemplate}
                        className="mt-3 inline-flex items-center text-sm font-medium text-blue-700 hover:text-blue-800"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                        Download CSV Template
                      </button>
                    </div>
                  </div>
                </div>

                {/* File Upload Area */}
                {!uploadResult && (
                  <div className="space-y-4">
                    {/* Progress Bar - shown during upload */}
                    {uploading && uploadProgress && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-700">{uploadProgress.message}</span>
                          <span className="text-indigo-600 font-semibold">{uploadProgress.percent}%</span>
                        </div>
                        <div className="relative w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                          <motion.div
                            className={`absolute top-0 left-0 h-full rounded-full ${
                              uploadProgress.stage === 'processing'
                                ? 'bg-gradient-to-r from-indigo-500 to-purple-500'
                                : uploadProgress.stage === 'complete'
                                ? 'bg-green-500'
                                : 'bg-indigo-600'
                            }`}
                            initial={{ width: 0 }}
                            animate={{ width: `${uploadProgress.percent}%` }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                          />
                          {uploadProgress.stage === 'processing' && (
                            <motion.div
                              className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-transparent via-white/30 to-transparent"
                              animate={{ x: ['-100%', '100%'] }}
                              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                            />
                          )}
                        </div>
                        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                          {uploadProgress.stage === 'uploading' && (
                            <>
                              <ArrowUpTrayIcon className="h-4 w-4 animate-bounce" />
                              <span>Uploading file to server...</span>
                            </>
                          )}
                          {uploadProgress.stage === 'processing' && (
                            <>
                              <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              <span>Creating teacher accounts and assigning lessons...</span>
                            </>
                          )}
                          {uploadProgress.stage === 'complete' && (
                            <>
                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                              <span>Processing complete!</span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* File Selection Area - hidden during upload */}
                    {!uploading && (
                      <>
                        <div
                          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                            uploadFile
                              ? 'border-green-300 bg-green-50'
                              : 'border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                          }`}
                        >
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleFileSelect}
                            className="hidden"
                            id="csv-upload"
                          />
                          <label htmlFor="csv-upload" className="cursor-pointer">
                            {uploadFile ? (
                              <div className="space-y-2">
                                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
                                <p className="text-sm font-medium text-green-700">{uploadFile.name}</p>
                                <p className="text-xs text-green-600">
                                  {(uploadFile.size / 1024).toFixed(1)} KB - Click to change file
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <ArrowUpTrayIcon className="h-12 w-12 text-gray-400 mx-auto" />
                                <p className="text-sm font-medium text-gray-700">
                                  Click to select a CSV file
                                </p>
                                <p className="text-xs text-gray-500">Maximum file size: 5MB</p>
                              </div>
                            )}
                          </label>
                        </div>

                        <div className="flex justify-end space-x-3">
                          <Button
                            type="button"
                            onClick={resetUploadModal}
                            variant="secondary"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleUploadTeachers}
                            disabled={!uploadFile || uploading}
                            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                          >
                            <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
                            Upload Teachers
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Upload Results */}
                {uploadResult && (
                  <div className="space-y-4">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {uploadResult.data.summary.totalProcessed}
                        </p>
                        <p className="text-sm text-gray-600">Total Processed</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-green-600">
                          {uploadResult.data.summary.successfulCount}
                        </p>
                        <p className="text-sm text-green-700">Successful</p>
                      </div>
                      <div className={`rounded-lg p-4 text-center ${
                        uploadResult.data.summary.failedCount > 0 ? 'bg-red-50' : 'bg-gray-50'
                      }`}>
                        <p className={`text-2xl font-bold ${
                          uploadResult.data.summary.failedCount > 0 ? 'text-red-600' : 'text-gray-400'
                        }`}>
                          {uploadResult.data.summary.failedCount}
                        </p>
                        <p className={`text-sm ${
                          uploadResult.data.summary.failedCount > 0 ? 'text-red-700' : 'text-gray-600'
                        }`}>Failed</p>
                      </div>
                    </div>

                    {/* Successful Records */}
                    {uploadResult.data.successful.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                          Successfully Created ({uploadResult.data.successful.length})
                        </h4>
                        <div className="max-h-40 overflow-y-auto border border-green-200 rounded-lg">
                          <table className="min-w-full divide-y divide-green-200">
                            <thead className="bg-green-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-green-700">Name</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-green-700">Email</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-green-700">Lessons</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-green-100">
                              {uploadResult.data.successful.map((teacher, idx) => (
                                <tr key={idx}>
                                  <td className="px-3 py-2 text-sm text-gray-900">{teacher.name}</td>
                                  <td className="px-3 py-2 text-sm text-gray-600">{teacher.email}</td>
                                  <td className="px-3 py-2 text-sm text-gray-600">{teacher.lessonsAssigned} assigned</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Failed Records */}
                    {uploadResult.data.failed.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-900 flex items-center">
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mr-2" />
                            Failed Records ({uploadResult.data.failed.length})
                          </h4>
                          <button
                            onClick={handleDownloadFailedRecords}
                            className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-700"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                            Download Failed Records
                          </button>
                        </div>
                        <div className="max-h-40 overflow-y-auto border border-red-200 rounded-lg">
                          <table className="min-w-full divide-y divide-red-200">
                            <thead className="bg-red-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-red-700">Row</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-red-700">Name</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-red-700">Email</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-red-700">Reason</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-red-100">
                              {uploadResult.data.failed.map((record, idx) => (
                                <tr key={idx}>
                                  <td className="px-3 py-2 text-sm text-gray-900">{record.row}</td>
                                  <td className="px-3 py-2 text-sm text-gray-900">{record.name}</td>
                                  <td className="px-3 py-2 text-sm text-gray-600">{record.email}</td>
                                  <td className="px-3 py-2 text-sm text-red-600">{record.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t">
                      <Button
                        type="button"
                        onClick={() => {
                          setUploadFile(null)
                          setUploadResult(null)
                          if (fileInputRef.current) {
                            fileInputRef.current.value = ''
                          }
                        }}
                        variant="secondary"
                      >
                        Upload Another File
                      </Button>
                      <Button
                        onClick={resetUploadModal}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TeachersPage